/**
 * SignalR Connection Service
 * Handles real-time communication with the Remote Desktop server
 */

import * as signalR from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import {
    AgentInfo,
    ConnectionState,
    ConnectionStatus,
    WebRTCSignal,
    SessionInfo,
    EncryptedFrame,
    EncryptedMessage,
    KeyExchangeRequest,
    KeyExchangeResponse,
    InputCommand,
    IceServersResponse
} from '../types';

export interface SignalRServiceEvents {
    onConnectionStateChange?: (status: ConnectionStatus) => void;
    onAgentListUpdated?: (agents: AgentInfo[]) => void;
    onSessionStarted?: (session: SessionInfo) => void;
    onSessionEnded?: (sessionId: string, reason: string) => void;
    onFrameReceived?: (sessionId: string, frameData: Uint8Array, width: number, height: number) => void;
    onFrameReceivedEx?: (frameData: Uint8Array, width: number, height: number, codec: number, isKeyFrame: boolean) => void;
    onEncryptedFrameReceived?: (sessionId: string, frame: EncryptedFrame) => void;
    onWebRTCSignal?: (sessionId: string, signal: WebRTCSignal) => void;
    onWebRTCConnectionEstablished?: (sessionId: string) => void;
    onWebRTCConnectionFailed?: (sessionId: string, reason: string) => void;
    onKeyExchangeRequest?: (sessionId: string, request: KeyExchangeRequest) => void;
    onKeyExchangeResponse?: (sessionId: string, response: KeyExchangeResponse) => void;
    // WC-1: Audio frame from agent
    onAudioFrameReceived?: (frame: { sessionId: string; sequenceNumber: number; timestamp: number; codec: number; data: Uint8Array; sampleRate: number; channels: number; durationMs: number }) => void;
    // WC-2: Clipboard data from agent (matches C# ClipboardData model)
    onClipboardDataReceived?: (data: { contentType: number; textContent?: string | null; format?: string; content?: string; sessionId?: string }) => void;
    // WC-3: File transfer events from agent
    onFileChunkReceived?: (chunk: { transferId: string; chunkIndex: number; totalChunks: number; data: Uint8Array; isLast: boolean }) => void;
    onFileTransferProgress?: (progress: { transferId: string; bytesTransferred: number; totalBytes: number; percent: number; status: string }) => void;
    onFileTransferComplete?: (complete: { transferId: string; fileName: string; fileSize: number; checksum: string }) => void;
    onBrowseDirectoryResponse?: (response: { path: string; entries: any[]; error?: string }) => void;
    onMonitorChanged?: (sessionId: string, monitorIndex: number, width: number, height: number) => void;
    onError?: (error: Error) => void;
}

export class SignalRService {
    private connection: signalR.HubConnection | null = null;
    private serverUrl: string;
    private hubPath: string;
    private events: SignalRServiceEvents;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private accessToken: string | null = null;

    public connectionId: string | null = null;
    public status: ConnectionStatus = ConnectionStatus.Disconnected;

    constructor(serverUrl: string, hubPath: string = '/remotedesktop', events: SignalRServiceEvents = {}) {
        this.serverUrl = serverUrl;
        this.hubPath = hubPath;
        this.events = events;
    }

    /**
     * Set access token for authenticated connections
     */
    setAccessToken(token: string): void {
        this.accessToken = token;
    }

    /**
     * Connect to the SignalR hub
     */
    async connect(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
            console.log('Already connected to SignalR hub');
            return;
        }

        this.updateStatus(ConnectionStatus.Connecting);

        try {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(`${this.serverUrl}${this.hubPath}`, {
                    accessTokenFactory: () => this.accessToken || '',
                    skipNegotiation: false,
                    transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: (retryContext) => {
                        if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
                            return null; // Stop reconnecting
                        }
                        // Exponential backoff: 0, 2, 4, 8, 16, 32 seconds (max)
                        return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 32000);
                    }
                })
                .withHubProtocol(new MessagePackHubProtocol())
                .configureLogging(signalR.LogLevel.Information)
                .build();

            this.setupEventHandlers();

            await this.connection.start();
            this.connectionId = this.connection.connectionId;
            this.reconnectAttempts = 0;
            this.updateStatus(ConnectionStatus.Connected);

            console.log(`Connected to SignalR hub with ID: ${this.connectionId}`);
        } catch (error) {
            console.error('Failed to connect to SignalR hub:', error);
            this.updateStatus(ConnectionStatus.Error);
            this.events.onError?.(error as Error);
            throw error;
        }
    }

    /**
     * Disconnect from the SignalR hub
     */
    async disconnect(): Promise<void> {
        if (this.connection) {
            try {
                await this.connection.stop();
            } catch (error) {
                console.error('Error disconnecting:', error);
            }
            this.connection = null;
            this.connectionId = null;
            this.updateStatus(ConnectionStatus.Disconnected);
        }
    }

    /**
     * Register as a web client viewer
     */
    async registerViewer(clientInfo: { clientName: string; platform: string }): Promise<void> {
        this.ensureConnected();
        // Create ViewerInfo object matching C# model
        const viewerInfo = {
            viewerId: '', // Will be set by server
            clientName: clientInfo.clientName,
            platform: clientInfo.platform,
            browserName: this.detectBrowser(),
            version: '1.0.0',
            userAgent: navigator.userAgent,
            isWebClient: true,
            ipAddress: null,
            connectedAt: new Date().toISOString()
        };
        await this.connection!.invoke('RegisterViewer', viewerInfo);
    }

    private detectBrowser(): string {
        const ua = navigator.userAgent;
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Safari')) return 'Safari';
        if (ua.includes('Edge')) return 'Edge';
        return 'Unknown';
    }

    /**
     * Get list of available agents
     */
    async getAgentList(): Promise<AgentInfo[]> {
        this.ensureConnected();
        return await this.connection!.invoke<AgentInfo[]>('GetAvailableAgents');
    }

    /**
     * Request a remote desktop session with an agent
     */
    async requestSession(agentId: string): Promise<SessionInfo | null> {
        this.ensureConnected();
        return await this.connection!.invoke<SessionInfo | null>('RequestSession', agentId);
    }

    /**
     * Request a remote desktop session using Agent ID + Password (TeamViewer-style).
     * Uses server-side password validation with brute-force protection.
     */
    async requestSessionWithCredentials(agentId: string, password: string): Promise<SessionInfo | null> {
        this.ensureConnected();
        return await this.connection!.invoke<SessionInfo | null>('RequestSessionWithCredentials', agentId, password);
    }

    /**
     * End a remote desktop session
     */
    async endSession(sessionId: string): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('EndSession', sessionId);
    }

    /**
     * Send input command to the agent
     */
    async sendInput(sessionId: string, command: InputCommand): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('SendInput', sessionId, command);
    }

    /**
     * Send encrypted input command
     */
    async sendEncryptedInput(sessionId: string, encryptedCommand: EncryptedMessage): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('SendEncryptedInput', sessionId, encryptedCommand);
    }

    /**
     * Initiate key exchange for E2E encryption
     */
    async initiateKeyExchange(sessionId: string, request: KeyExchangeRequest): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('InitiateKeyExchange', sessionId, request);
    }

    /**
     * Send key exchange response
     */
    async sendKeyExchangeResponse(sessionId: string, response: KeyExchangeResponse): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('SendKeyExchangeResponse', sessionId, response);
    }

    /**
     * Send WebRTC signaling message
     */
    async sendWebRTCSignal(sessionId: string, signal: WebRTCSignal): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('SendWebRTCSignal', sessionId, signal);
    }

    /**
     * Get ICE servers (STUN/TURN) for WebRTC connection
     */
    async getIceServers(): Promise<IceServersResponse> {
        this.ensureConnected();
        return await this.connection!.invoke<IceServersResponse>('GetIceServers');
    }

    /**
     * Switch to a different monitor
     */
    async selectMonitor(sessionId: string, monitorIndex: number): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('SelectMonitor', sessionId, monitorIndex);
    }

    /**
     * Request quality change
     */
    async requestQualityChange(sessionId: string, quality: number, fps: number): Promise<void> {
        this.ensureConnected();
        await this.connection!.invoke('RequestQualityChange', sessionId, quality, fps);
    }

    /**
     * Invoke a hub method with typed access (WC-17: replaces `as any` casts)
     */
    async invoke<T = any>(method: string, ...args: any[]): Promise<T> {
        this.ensureConnected();
        return await this.connection!.invoke<T>(method, ...args);
    }

    /**
     * Setup event handlers for SignalR hub methods
     */
    private setupEventHandlers(): void {
        if (!this.connection) return;

        // Connection lifecycle events
        this.connection.onreconnecting((error) => {
            console.log('SignalR reconnecting...', error);
            this.updateStatus(ConnectionStatus.Reconnecting);
        });

        this.connection.onreconnected((connectionId) => {
            console.log('SignalR reconnected with ID:', connectionId);
            this.connectionId = connectionId || null;
            this.updateStatus(ConnectionStatus.Connected);
        });

        this.connection.onclose((error) => {
            console.log('SignalR connection closed', error);
            this.connectionId = null;
            this.updateStatus(ConnectionStatus.Disconnected);
        });

        // Hub method handlers

        // Agent list updates
        this.connection.on('AgentListUpdated', (agents: AgentInfo[]) => {
            this.events.onAgentListUpdated?.(agents);
        });

        // Session started
        this.connection.on('SessionStarted', (session: SessionInfo) => {
            this.events.onSessionStarted?.(session);
        });

        // Session ended
        this.connection.on('SessionEnded', (sessionId: string, reason: string) => {
            this.events.onSessionEnded?.(sessionId, reason);
        });

        // Frame received (SignalR relay mode) - JPEG frames
        // Note: C# interface is ReceiveFrame(byte[] frameData, int width, int height) - no sessionId
        // SignalR JSON protocol sends byte[] as base64 string
        this.connection.on('ReceiveFrame', (frameData: string | Uint8Array, width: number, height: number) => {
            const binaryData = this.toBinaryData(frameData);
            if (binaryData && binaryData.length > 0) {
                this.events.onFrameReceived?.('', binaryData, width, height);
            }
        });

        // Extended frame received with codec info (H.264/VP8/JPEG)
        this.connection.on('ReceiveFrameEx', (frameData: string | Uint8Array, width: number, height: number, codec: number, isKeyFrame: boolean) => {
            const binaryData = this.toBinaryData(frameData);
            if (binaryData && binaryData.length > 0) {
                this.events.onFrameReceivedEx?.(binaryData, width, height, codec, isKeyFrame);
            }
        });

        // Encrypted frame received
        this.connection.on('ReceiveEncryptedFrame', (sessionId: string, frame: EncryptedFrame) => {
            this.events.onEncryptedFrameReceived?.(sessionId, frame);
        });

        // WebRTC signaling
        this.connection.on('ReceiveWebRTCSignal', (sessionId: string, signal: WebRTCSignal) => {
            this.events.onWebRTCSignal?.(sessionId, signal);
        });

        // HIGH-7: WebRTC connection status callbacks
        this.connection.on('WebRTCConnectionEstablished', (sessionId: string) => {
            console.log(`[SignalR] WebRTC P2P established for session ${sessionId}`);
            this.events.onWebRTCConnectionEstablished?.(sessionId);
        });

        this.connection.on('WebRTCConnectionFailed', (sessionId: string, reason: string) => {
            console.log(`[SignalR] WebRTC P2P failed for session ${sessionId}: ${reason}`);
            this.events.onWebRTCConnectionFailed?.(sessionId, reason);
        });

        // Key exchange
        this.connection.on('ReceiveKeyExchangeRequest', (sessionId: string, request: KeyExchangeRequest) => {
            this.events.onKeyExchangeRequest?.(sessionId, request);
        });

        this.connection.on('ReceiveKeyExchangeResponse', (sessionId: string, response: KeyExchangeResponse) => {
            this.events.onKeyExchangeResponse?.(sessionId, response);
        });

        // Monitor changed notification
        this.connection.on('MonitorChanged', (sessionId: string, monitorIndex: number, width: number, height: number) => {
            console.log(`Monitor changed: ${monitorIndex} (${width}x${height})`);
            this.events.onMonitorChanged?.(sessionId, monitorIndex, width, height);
        });

        // WC-1: Audio frame from agent
        this.connection.on('ReceiveAudioFrame', (frame: any) => {
            if (frame?.data) {
                frame.data = this.toBinaryData(frame.data);
            }
            this.events.onAudioFrameReceived?.(frame);
        });

        // WC-2: Clipboard data from agent
        this.connection.on('ReceiveClipboardData', (data: any) => {
            this.events.onClipboardDataReceived?.(data);
        });

        // WC-3: File transfer handlers
        this.connection.on('ReceiveFileChunk', (chunk: any) => {
            if (chunk?.data) {
                chunk.data = this.toBinaryData(chunk.data);
            }
            this.events.onFileChunkReceived?.(chunk);
        });

        this.connection.on('ReceiveFileTransferProgress', (progress: any) => {
            this.events.onFileTransferProgress?.(progress);
        });

        this.connection.on('ReceiveFileTransferComplete', (complete: any) => {
            this.events.onFileTransferComplete?.(complete);
        });

        this.connection.on('ReceiveBrowseDirectoryResponse', (response: any) => {
            this.events.onBrowseDirectoryResponse?.(response);
        });
    }

    /**
     * Update connection status and notify listeners
     */
    private updateStatus(status: ConnectionStatus): void {
        this.status = status;
        this.events.onConnectionStateChange?.(status);
    }

    /**
     * Ensure connection is active
     */
    private ensureConnected(): void {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            throw new Error('Not connected to SignalR hub');
        }
    }

    /**
     * Check if connected
     */
    get isConnected(): boolean {
        return this.connection?.state === signalR.HubConnectionState.Connected;
    }

    /**
     * Convert SignalR data to binary Uint8Array
     * SignalR JSON protocol sends byte[] as base64 string
     */
    private toBinaryData(data: string | Uint8Array | ArrayBuffer): Uint8Array {
        if (data instanceof Uint8Array) {
            return data;
        }
        if (data instanceof ArrayBuffer) {
            return new Uint8Array(data);
        }
        if (typeof data === 'string') {
            // Fast base64 decode using fetch API (avoids byte-by-byte loop)
            try {
                const binaryString = atob(data);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                // Use TextEncoder trick for faster decode when available
                // Process 4 bytes at a time for better performance
                let i = 0;
                for (; i + 3 < len; i += 4) {
                    bytes[i] = binaryString.charCodeAt(i);
                    bytes[i + 1] = binaryString.charCodeAt(i + 1);
                    bytes[i + 2] = binaryString.charCodeAt(i + 2);
                    bytes[i + 3] = binaryString.charCodeAt(i + 3);
                }
                for (; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                return bytes;
            } catch {
                console.warn('[SignalR] Failed to decode base64 data');
                return new Uint8Array(0);
            }
        }
        // Unknown format, try to convert
        console.warn('[SignalR] Unknown frame data format:', typeof data);
        return new Uint8Array(0);
    }
}
